import { Thermometer, Wind, Droplets, Cloud, CloudRain } from 'lucide-react';
import { useOrgTimezone, formatDateTimeInTz } from '@/utils/timezone';
import { toNum, type Numeric } from '@/utils/numeric';

interface WeatherData {
  temp_c?: Numeric;
  wind_kph?: Numeric;
  wind_dir?: string | null;
  humidity?: Numeric;
  precip_mm?: Numeric;
  conditions?: string | null;
  fetched_at?: string | null;
  source?: string | null;
}

/**
 * Which snapshot a column holds. A spray record carries three, each frozen
 * when it was taken: the forecast at creation, conditions at start, and
 * conditions at sign-off. `end` reads the wx_actual_* columns, which are the
 * completion snapshot under an older name.
 */
type Phase = 'forecast' | 'start' | 'end';

interface WeatherPanelProps {
  forecast?: WeatherData | null;
  actual?: WeatherData | null;
  // Pass individual snapshot fields from spray_applications directly
  forecastFromSpray?: {
    wx_forecast_temp_c?: Numeric;
    wx_forecast_wind_kph?: Numeric;
    wx_forecast_wind_dir?: string | null;
    wx_forecast_humidity?: Numeric;
    wx_forecast_precip_mm?: Numeric;
    wx_forecast_conditions?: string | null;
    wx_forecast_fetched_at?: string | null;
    wx_start_temp_c?: Numeric;
    wx_start_wind_kph?: Numeric;
    wx_start_wind_dir?: string | null;
    wx_start_humidity?: Numeric;
    wx_start_precip_mm?: Numeric;
    wx_start_conditions?: string | null;
    wx_start_source?: string | null;
    wx_start_fetched_at?: string | null;
    wx_actual_temp_c?: Numeric;
    wx_actual_wind_kph?: Numeric;
    wx_actual_wind_dir?: string | null;
    wx_actual_humidity?: Numeric;
    wx_actual_precip_mm?: Numeric;
    wx_actual_conditions?: string | null;
    wx_actual_source?: string | null;
    wx_actual_fetched_at?: string | null;
  };
  // Live current-conditions from /current-weather endpoint
  liveWeather?: { source: string; snapshot: Record<string, unknown> } | null;
  // Drives the empty-state wording only: a blank column on a finished spray
  // means the reading was never captured, not that it is still coming.
  status?: string | null;
}

function SourceBadge({ source }: { source?: string | null }) {
  if (!source) return null;
  const isStation = source === 'station' || source === 'weather_station';
  const isMeteo = source === 'open_meteo';
  // Anything unrecognised prints itself rather than being filed under
  // Open-Meteo. A wrong provenance label is worse than an ugly one.
  if (!isStation && !isMeteo) {
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-neutral-100 text-neutral-600">
        {source}
      </span>
    );
  }
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
      isStation ? 'bg-green-100 text-green-700' : 'bg-sky-100 text-sky-700'
    }`}>
      {isStation ? 'On-farm station' : 'Open-Meteo'}
    </span>
  );
}

/**
 * Whether a spray has passed the point at which this snapshot would be taken.
 * A cancelled spray counts as past everything: whatever it holds is all it
 * will ever hold.
 */
function phaseReached(phase: Phase, status?: string | null): boolean {
  if (!status) return false;
  if (status === 'cancelled') return true;
  if (phase === 'forecast') return true;
  if (phase === 'start') return ['in_progress', 'paused', 'completed'].includes(status);
  return status === 'completed';
}

function emptyCopy(phase: Phase, status?: string | null): string {
  if (phase === 'forecast') return 'Forecast not available';
  return phaseReached(phase, status) ? 'Not recorded' : 'Not yet recorded';
}

function WeatherColumn({
  phase, data, status,
}: { phase: Phase; data: WeatherData | null; status?: string | null }) {
  const orgTz = useOrgTimezone();

  const tempC = toNum(data?.temp_c);
  const windKph = toNum(data?.wind_kph);
  const humidity = toNum(data?.humidity);
  const precipMm = toNum(data?.precip_mm);

  if (!data || (tempC == null && !data.conditions)) {
    return (
      <div className="text-center py-6 text-neutral-400 text-sm">
        <Cloud className="h-8 w-8 mx-auto mb-2 opacity-50" />
        {emptyCopy(phase, status)}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {(data.conditions || data.source) && (
        <div className="flex items-center gap-2 flex-wrap">
          {data.conditions && (
            <p className="text-sm font-medium text-neutral-700 break-words min-w-0">{data.conditions}</p>
          )}
          <SourceBadge source={data.source} />
        </div>
      )}
      <div className="grid grid-cols-2 gap-2">
        {tempC != null && (
          <div className="flex items-center gap-2 min-w-0">
            <Thermometer className="h-4 w-4 shrink-0 text-red-400" />
            <div className="min-w-0">
              <p className="text-xs text-neutral-400">Temp</p>
              <p className="text-sm font-semibold text-neutral-800">{tempC.toFixed(1)}°C</p>
            </div>
          </div>
        )}
        {windKph != null && (
          <div className="flex items-center gap-2 min-w-0">
            <Wind className="h-4 w-4 shrink-0 text-blue-400" />
            <div className="min-w-0">
              <p className="text-xs text-neutral-400">Wind</p>
              <p className="text-sm font-semibold text-neutral-800 break-words">
                {windKph.toFixed(0)} km/h {data.wind_dir || ''}
              </p>
            </div>
          </div>
        )}
        {humidity != null && (
          <div className="flex items-center gap-2 min-w-0">
            <Droplets className="h-4 w-4 shrink-0 text-blue-300" />
            <div className="min-w-0">
              <p className="text-xs text-neutral-400">Humidity</p>
              <p className="text-sm font-semibold text-neutral-800">{humidity.toFixed(0)}%</p>
            </div>
          </div>
        )}
        {precipMm != null && (
          <div className="flex items-center gap-2 min-w-0">
            <CloudRain className="h-4 w-4 shrink-0 text-blue-500" />
            <div className="min-w-0">
              <p className="text-xs text-neutral-400">Precip</p>
              <p className="text-sm font-semibold text-neutral-800">{precipMm.toFixed(1)} mm</p>
            </div>
          </div>
        )}
      </div>
      {data.fetched_at && (
        <p className="text-xs text-neutral-400 break-words">
          {phase === 'forecast' ? 'Fetched' : 'Observed'} {formatDateTimeInTz(data.fetched_at, orgTz)}
        </p>
      )}
    </div>
  );
}

function hasReading(data: WeatherData | null): boolean {
  return !!data && (!!data.conditions || toNum(data.temp_c) != null);
}

function WeatherPanel({ forecast, actual, forecastFromSpray, liveWeather, status }: WeatherPanelProps) {
  // Normalise from the spray_applications flat fields if passed directly
  const forecastData: WeatherData | null = forecast || (forecastFromSpray
    ? {
        temp_c:    forecastFromSpray.wx_forecast_temp_c,
        wind_kph:  forecastFromSpray.wx_forecast_wind_kph,
        wind_dir:  forecastFromSpray.wx_forecast_wind_dir,
        humidity:  forecastFromSpray.wx_forecast_humidity,
        precip_mm: forecastFromSpray.wx_forecast_precip_mm,
        conditions: forecastFromSpray.wx_forecast_conditions,
        fetched_at: forecastFromSpray.wx_forecast_fetched_at,
      }
    : null);

  const startData: WeatherData | null = forecastFromSpray
    ? {
        temp_c:    forecastFromSpray.wx_start_temp_c,
        wind_kph:  forecastFromSpray.wx_start_wind_kph,
        wind_dir:  forecastFromSpray.wx_start_wind_dir,
        humidity:  forecastFromSpray.wx_start_humidity,
        precip_mm: forecastFromSpray.wx_start_precip_mm,
        conditions: forecastFromSpray.wx_start_conditions,
        fetched_at: forecastFromSpray.wx_start_fetched_at,
        source:     forecastFromSpray.wx_start_source,
      }
    : null;

  const actualData: WeatherData | null = actual || (forecastFromSpray
    ? {
        temp_c:    forecastFromSpray.wx_actual_temp_c,
        wind_kph:  forecastFromSpray.wx_actual_wind_kph,
        wind_dir:  forecastFromSpray.wx_actual_wind_dir,
        humidity:  forecastFromSpray.wx_actual_humidity,
        precip_mm: forecastFromSpray.wx_actual_precip_mm,
        conditions: forecastFromSpray.wx_actual_conditions,
        fetched_at: forecastFromSpray.wx_actual_fetched_at,
        source:     forecastFromSpray.wx_actual_source,
      }
    : null);

  const hasForecast = hasReading(forecastData);
  const hasStart = hasReading(startData);
  const hasActual = hasReading(actualData);
  const hasLive = liveWeather?.snapshot != null;

  // When no stored data exists but we have live data, show it in place of "not available"
  const liveSnapshot: WeatherData | null = liveWeather
    ? { ...(liveWeather.snapshot as WeatherData), source: liveWeather.source }
    : null;

  if (!hasForecast && !hasStart && !hasActual && !hasLive) {
    return (
      <div className="bg-neutral-50 rounded-lg p-4 text-center text-sm text-neutral-400">
        <Cloud className="h-6 w-6 mx-auto mb-1 opacity-40" />
        Weather data not available
      </div>
    );
  }

  // Live conditions sit above the record and are never part of it: they are
  // polled while a spray is active and stored nowhere.
  return (
    <div className="space-y-4">
      {hasLive && (
        <div className="bg-amber-50 rounded-lg p-4">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-amber-700 mb-3">Current Conditions</h4>
          <WeatherColumn phase="end" data={liveSnapshot} status={status} />
        </div>
      )}
      {(hasForecast || hasStart || hasActual) && (
        // Three across only once there is room for three. A third of 320px
        // cannot hold a two-column metric grid, so at phone width they stack
        // rather than run off the side of the screen.
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="bg-blue-50 rounded-lg p-4 min-w-0">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-blue-600 mb-3">Forecast</h4>
            <WeatherColumn phase="forecast" data={forecastData} status={status} />
          </div>
          <div className="bg-emerald-50 rounded-lg p-4 min-w-0">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-emerald-600 mb-3">At start</h4>
            <WeatherColumn phase="start" data={startData} status={status} />
          </div>
          <div className="bg-green-50 rounded-lg p-4 min-w-0">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-green-600 mb-3">At completion</h4>
            <WeatherColumn phase="end" data={actualData} status={status} />
          </div>
        </div>
      )}
    </div>
  );
}

export default WeatherPanel;
